import { QSingle, Random } from '@educraft/base';
import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfPen,
  PdfColor,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutSingleGen } from '../layoutSingle';
import { OptsSingle } from '../optsSingle';
import { GUtil } from '../../pdf/gutil';
import { genSingle, fixGenArray } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/helpers';

// ─── Data model ──────────────────────────────────────────────────────────────

class PuzzleEdge {
  label: string | null = null;
  isCorrectPath = false;
  constructor(
    public readonly from: PuzzleNode,
    public readonly to: PuzzleNode
  ) {}
}

class PuzzleNode {
  isStart = false;
  isEnd = false;
  expression: string[] | null = null;
  result: string | null = null;
  edges: PuzzleEdge[] = [];
  constructor(
    public readonly row: number,
    public readonly col: number
  ) {}
}

class PuzzleGrid {
  readonly nodes: PuzzleNode[][];

  constructor(
    public readonly cols: number,
    public readonly rows: number
  ) {
    this.nodes = Array.from({ length: rows }, (_, r) =>
      Array.from({ length: cols }, (_, c) => new PuzzleNode(r, c))
    );
    this.start.isStart = true;
    this.end.isEnd = true;
  }

  get start(): PuzzleNode {
    return this.nodes[0][0];
  }
  get end(): PuzzleNode {
    return this.nodes[this.rows - 1][this.cols - 1];
  }
  get flat(): PuzzleNode[] {
    return this.nodes.flat();
  }
}

// ─── Grid-building algorithm ──────────────────────────────────────────────────

function buildGrid(grid: PuzzleGrid, includeDiagonal: boolean): void {
  function addBidir(a: PuzzleNode, b: PuzzleNode): void {
    if (!a.edges.some((e) => e.to === b)) a.edges.push(new PuzzleEdge(a, b));
    if (!b.edges.some((e) => e.to === a)) b.edges.push(new PuzzleEdge(b, a));
  }

  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      const node = grid.nodes[r][c];
      if (c < grid.cols - 1) addBidir(node, grid.nodes[r][c + 1]);
      if (r < grid.rows - 1) addBidir(node, grid.nodes[r + 1][c]);

      if (includeDiagonal) {
        if (c % 2 === 0) {
          // Even columns: diagonal down-right
          if (r < grid.rows - 1 && c < grid.cols - 1) addBidir(node, grid.nodes[r + 1][c + 1]);
        } else {
          // Odd columns: diagonal up-right (avoids crossing)
          if (r > 0 && c < grid.cols - 1) addBidir(node, grid.nodes[r - 1][c + 1]);
        }
      }
    }
  }
}

// ─── Path & label algorithm ───────────────────────────────────────────────────

const trimAnswer = (s: string) => s.replace(/=+\s*$/, '').trim();

function assignAllNodes(grid: PuzzleGrid, equations: QSingle[]): void {
  const all = grid.flat;
  for (let i = 0; i < all.length; i++) {
    const node = all[i];
    const eq = equations[i % equations.length];
    node.result = trimAnswer(eq.answer);
    node.expression = fixGenArray(eq.texts).map(trimAnswer);
  }
}

function generateRandomPath(grid: PuzzleGrid, random: Random): PuzzleNode[] {
  let bestPath: PuzzleNode[] = [];
  const MAX_WALKS = 300;

  for (let attempt = 0; attempt < MAX_WALKS; attempt++) {
    const visited = new Set<PuzzleNode>();
    const path: PuzzleNode[] = [];
    let current: PuzzleNode | null = grid.start;

    while (current !== null) {
      visited.add(current);
      path.push(current);
      if (current === grid.end) break;

      const candidates: PuzzleNode[] = current.edges
        .map((e) => e.to)
        .filter((n) => !visited.has(n));
      if (candidates.length === 0) break;

      // Greedy: pick node farthest from end (with random noise for variation)
      const scored: { node: PuzzleNode; score: number }[] = candidates.map((n) => ({
        node: n,
        score: Math.abs(n.row - grid.end.row) + Math.abs(n.col - grid.end.col) + random.next(-2, 3),
      }));
      scored.sort((a, b) => b.score - a.score);
      current = scored[0].node;
    }

    if (path[path.length - 1] === grid.end && path.length > bestPath.length) {
      bestPath = [...path];
    }
  }

  return bestPath.length > 0 ? bestPath : [grid.start, grid.end];
}

function markCorrectPath(grid: PuzzleGrid, path: PuzzleNode[]): void {
  for (const node of grid.flat) {
    node.isStart = node === grid.start;
    node.isEnd = node === grid.end;
    for (const edge of node.edges) edge.isCorrectPath = false;
  }
  for (let i = 0; i < path.length - 1; i++) {
    const edge = path[i].edges.find((e) => e.to === path[i + 1]);
    if (edge) edge.isCorrectPath = true;
  }
}

function assignEdgeLabels(
  grid: PuzzleGrid,
  path: PuzzleNode[],
  equations: QSingle[],
  random: Random
): void {
  const allAnswers = [...new Set(equations.map((eq) => trimAnswer(eq.answer)))];

  // Phase 1: path edges → source node's answer
  for (let i = 0; i < path.length - 1; i++) {
    const u = path[i];
    const v = path[i + 1];
    const e1 = u.edges.find((e) => e.to === v);
    const e2 = v.edges.find((e) => e.to === u);
    if (e1) e1.label = u.result;
    if (e2) e2.label = u.result;
  }

  // Phase 2: remaining edges → wrong answers
  for (const u of grid.flat) {
    const usedLabels = new Set(u.edges.filter((e) => e.label !== null).map((e) => e.label!));
    for (const edge of u.edges) {
      if (edge.label !== null) continue;

      const v = edge.to;
      const vEdge = v.edges.find((e) => e.to === u);
      if (vEdge?.label != null) {
        edge.label = vEdge.label;
        continue;
      }

      const wrong = allAnswers.filter(
        (a) => a !== u.result && a !== v.result && !usedLabels.has(a)
      );
      let label: string;
      if (wrong.length > 0) {
        label = wrong[random.next(0, wrong.length)];
      } else {
        do {
          label = String(random.next(1, 100));
        } while (label === u.result || label === v.result || usedLabels.has(label));
      }

      edge.label = label;
      if (vEdge) vEdge.label = label;
      usedLabels.add(label);
    }
  }
}

// ─── Rendering ────────────────────────────────────────────────────────────────

const THICK_PEN = new PdfPen(PdfColor.fromRgb(0, 0, 0), 4);

function renderGrid(page: PageGen, grid: PuzzleGrid): void {
  const margin = PdfUnit.FromMillimeters(10);
  const cellSize = Math.min(
    (PdfSize.A4.width - 2 * margin) / grid.cols,
    (PdfSize.A4.height - 2 * margin) / grid.rows
  );

  const offsetX = (PdfSize.A4.width - grid.cols * cellSize) / 2;
  const offsetY = (PdfSize.A4.height - grid.rows * cellSize) / 2;
  const ox = offsetX + cellSize / 2;
  const oy = offsetY + cellSize / 2;

  const nodeSize = cellSize * 0.6;
  const labelSize = cellSize * 0.25;
  const cornerRadius = cellSize * 0.1;
  const labelCorner = labelSize * 0.2;

  // Font adjustment
  const allExpressions = grid.flat
    .filter((n) => n.expression !== null)
    .flatMap((n) => n.expression!);
  const allLabels = grid.flat
    .flatMap((n) => n.edges)
    .filter((e) => e.label !== null)
    .map((e) => e.label!);

  const nodeFont =
    allExpressions.length > 0
      ? page.adjustFont(nodeSize * 0.97, allExpressions, GUtil.baseFont)
      : GUtil.baseFont;
  const labelFont =
    allLabels.length > 0
      ? page.adjustFont(labelSize * 0.97, allLabels, GUtil.baseFont)
      : GUtil.baseFont;

  // ── Draw edges (lines + midpoint label boxes) ──
  const processed = new Set<PuzzleEdge>();

  for (const node of grid.flat) {
    const x = ox + node.col * cellSize;
    const y = oy + node.row * cellSize;

    for (const edge of node.edges) {
      if (processed.has(edge)) continue;

      const reverse = edge.to.edges.find((e) => e.to === edge.from);
      processed.add(edge);
      if (reverse) processed.add(reverse);

      const x2 = ox + edge.to.col * cellSize;
      const y2 = oy + edge.to.row * cellSize;

      page.drawLine(GUtil.basePen, new PdfPoint(x, y), new PdfPoint(x2, y2));

      const label = edge.label ?? reverse?.label ?? null;
      if (label !== null) {
        const mx = (x + x2) / 2;
        const my = (y + y2) / 2;
        const labelRect = new PdfRect(mx - labelSize / 2, my - labelSize / 2, labelSize, labelSize);
        page.drawRoundedRectangle(null, PdfBrush.White, labelRect, labelCorner);
        page.drawRoundedRectangle(GUtil.basePen, null, labelRect, labelCorner);
        page.drawString(label, labelFont, PdfBrush.Black, null, labelRect, ETextAlign.Center);
      }
    }
  }

  // ── Draw nodes ──
  for (const node of grid.flat) {
    const x = ox + node.col * cellSize;
    const y = oy + node.row * cellSize;
    const rect = new PdfRect(x - nodeSize / 2, y - nodeSize / 2, nodeSize, nodeSize);

    const pen = node.isStart || node.isEnd ? THICK_PEN : GUtil.basePen;
    page.drawRoundedRectangle(null, PdfBrush.White, rect, cornerRadius);
    page.drawRoundedRectangle(pen, null, rect, cornerRadius);

    // End node is intentionally left blank (no expression)
    if (!node.isEnd && node.expression && node.expression.length > 0) {
      page.drawStrings(
        node.expression,
        nodeFont,
        PdfBrush.Black,
        null,
        rect.deflate(2),
        ETextAlign.Center
      );
    }
  }
}

// ─── Generator base ───────────────────────────────────────────────────────────

function generateMathPath(
  pdf: PdfGen,
  opts: OptsSingle,
  cols: number,
  rows: number,
  diagonal: boolean
): void {
  const totalSheets = 1;

  for (let sIdx = 0; sIdx < totalSheets; sIdx++) {
    const grid = new PuzzleGrid(cols, rows);
    buildGrid(grid, diagonal);

    const equations = genSingle(opts.data, opts.generator, grid.rows * grid.cols, true, true);

    assignAllNodes(grid, equations);
    const path = generateRandomPath(grid, opts.data.random);
    markCorrectPath(grid, path);
    assignEdgeLabels(grid, path, equations, opts.data.random);

    renderGrid(pdf.addPage(), grid);
  }
}

// ─── Public classes ───────────────────────────────────────────────────────────

/**
 * MathPath__Base — 4-column × 6-row path puzzle without diagonal edges.
 * Port of C# EduFrame.Single.Generators.MathPath__Base
 */
export class MathPath__Base implements ILayoutSingleGen {
  public async generate(pdf: PdfGen, opts: OptsSingle): Promise<void> {
    generateMathPath(pdf, opts, 4, 6, false);
  }
}

/**
 * MathPath__Diagonal — 6-column × 8-row path puzzle with diagonal edges.
 * Port of C# EduFrame.Single.Generators.MathPath__Diagonal
 */
export class MathPath__Diagonal implements ILayoutSingleGen {
  public async generate(pdf: PdfGen, opts: OptsSingle): Promise<void> {
    generateMathPath(pdf, opts, 6, 8, true);
  }
}

/**
 * MathPath__Small — 3-column × 4-row path puzzle without diagonal edges.
 * Port of C# EduFrame.Single.Generators.MathPath__Small
 */
export class MathPath__Small implements ILayoutSingleGen {
  public async generate(pdf: PdfGen, opts: OptsSingle): Promise<void> {
    generateMathPath(pdf, opts, 3, 4, false);
  }
}
